/**
 * HOF-216 AC 9 — the unlink lane.
 *
 * Two facts the fleet cannot otherwise see, and both are about an affordance
 * rather than an error, so no existing lane carries them:
 *
 *  * ``unlink`` — a confirmed unlink landed. Making an existing mutation
 *    reachable raises its call rate; this is how that rate becomes visible at
 *    all, and ``trigger`` is what says whether the new controls were what made
 *    it reachable or whether the keyboard shortcut was enough all along.
 *  * ``inert_edge`` — a delete gesture landed on an edge that STILL has no
 *    unlink at any layer (an Abrechnung edge, a Zahlungsavis edge, a stub).
 *    HOF-219 gave the GS↔LS document edge one, so that edge no longer counts
 *    here — leaving it would have made the demand signal for a feature that
 *    now exists keep rising.
 *  * ``document_unlink`` — a confirmed DOCUMENT-level sever landed (HOF-219).
 *    Its own kind rather than a ``trigger`` on ``unlink``, because the two are
 *    different mutations against different endpoints: one edits ``line_pairs``,
 *    the other writes a durable exclusion that can split a group into two
 *    tiles. Sharing a kind would make the line-unlink rate unreadable.
 *  * ``document_unlink_rejected`` — the backend refused one (AC 15). The more
 *    interesting half: ``stale_unlink`` at any volume means the canvas and the
 *    twin disagree often enough that operators are acting on stale views.
 *
 * Deliberately NOT a new ``LineConnectionVerdict``: that union is pinned to
 * the backend ``Literal`` by ``test_link_reject_verdicts_match_the_frontend_
 * union``, and an unlink is not a rejected connection — folding it in would
 * corrupt the reject-rate metric HOF-90 needed that lane for.
 */
import { postTelemetryBeacon } from '../../../services/telemetry';

/**
 * Closed vocabulary, mirrored by ``LineUnlinkTelemetryEvent.kind`` and pinned
 * by ``backend/tests/unit/api/test_telemetry.py::
 * test_line_unlink_vocabularies_match_the_frontend_unions``. The poster is
 * fire-and-forget, so drift is a SILENT 422 that reads as "operators never do
 * that" — add a value on both sides in one PR.
 */
export type LineUnlinkKind = 'unlink' | 'inert_edge' | 'document_unlink' | 'document_unlink_rejected';

/** Which affordance the operator reached for. Same pinning as ``LineUnlinkKind``. */
export type LineUnlinkTrigger = 'edge_control' | 'context_menu' | 'keyboard';

export interface LineUnlinkTelemetryPayload {
  kind: LineUnlinkKind;
  trigger: LineUnlinkTrigger;
  /** The match group. ``null`` when no endpoint resolves — itself the signal. */
  group_id: string | null;
}

// Explicit ``/v1`` for the BFF's generic ``/api/v1/{path}`` proxy route, and
// under ``/telemetry/`` so both CSRF middlewares exempt it — sendBeacon
// cannot set ``X-CSRF-Token``. Same two constraints as the sibling events.
export const LINE_UNLINK_TELEMETRY_URL = '/api/v1/telemetry/line-unlink';

/**
 * Fire-and-forget POST of one unlink-lane event. Never throws and never
 * surfaces anything: AC 9 requires that the emission cannot block, delay or
 * fail the operator's action, and ``postTelemetryBeacon`` already swallows
 * every transport error.
 */
export function postLineUnlinkTelemetry(payload: LineUnlinkTelemetryPayload): void {
  postTelemetryBeacon(LINE_UNLINK_TELEMETRY_URL, payload);
}
